import {
  useEffect, useState, useRef, useCallback, ReactNode,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type HorizontalScrollContainerProps = {
  children: ReactNode
  className?: string
};

export const HorizontalScrollContainer = ({ children, className = "" }: HorizontalScrollContainerProps) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const newCanScrollLeft = scrollLeft > 0;
      const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

      setCanScrollLeft(newCanScrollLeft);
      setCanScrollRight(newCanScrollRight);
    }
  }, []);

  const getScrollAmount = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const children = container.children;

      if (children.length === 0) return 300;

      const containerWidth = container.clientWidth;
      const isMobile = containerWidth < 768;

      if (isMobile) {
        const firstChild = children[0] as HTMLElement;
        const gap = 8; // gap-2 = 8px
        return firstChild.offsetWidth + gap;
      }

      let totalWidth = 0;
      let visibleCount = 0;

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const rect = child.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        if (rect.left < containerRect.right && rect.right > containerRect.left) {
          totalWidth += child.offsetWidth;
          visibleCount++;
        }
      }

      if (visibleCount === 0 && children.length > 0) {
        return (children[0] as HTMLElement).offsetWidth;
      }

      return visibleCount > 0 ? totalWidth / visibleCount : 300;
    }

    return 300;
  }, []);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      const scrollAmount = getScrollAmount();
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const targetScroll = Math.max(0, currentScroll - scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  }, [getScrollAmount]);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const scrollAmount = getScrollAmount();
      const maxScroll = scrollWidth - clientWidth;
      const targetScroll = Math.min(maxScroll, scrollLeft + scrollAmount);

      scrollContainerRef.current.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });
    }
  }, [getScrollAmount]);

  useEffect(() => {
    const handleResize = () => {
      setTimeout(checkScrollButtons, 100);
    };

    checkScrollButtons();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [checkScrollButtons]);

  return (
    <div className={`relative scroll-indicator ${className}`}>
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-10 dark:bg-gray-800/90 rounded-full border border-gray-200 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 scroll-nav-button"
          aria-label="Faire défiler vers la gauche"
        >
          <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      )}

      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 dark:bg-gray-800/90 rounded-full border border-gray-200 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 scroll-nav-button"
          aria-label="Faire défiler vers la droite"
        >
          <ChevronRight size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="flex gap-2 md:gap-4 h-full overflow-x-auto scrollbar-thin"
        onScroll={checkScrollButtons}
      >
        {children}
      </div>
    </div>
  );
};
